import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { IInstrument, TradeOrderSideEnum, isFiat } from '@sparling/financial-common'
import { instrumentsSelector } from '../store/selectors'

interface UseCommissionPreviewParams {
  instrument: IInstrument
  side: TradeOrderSideEnum
  quantity: number
  commissionRate: number
}

interface UseCommissionPreviewResult {
  commissionAmount: number
  commissionCurrency: string
  commissionInQuote: number
  isCryptoCrypto: boolean
  isCommissionInQuote: boolean
  amountInQuote: number
  price: number
  base: string
  quote: string
}

export function useCommissionPreview({
  instrument,
  side,
  quantity,
  commissionRate,
}: UseCommissionPreviewParams): UseCommissionPreviewResult | null {
  const instruments = useSelector(instrumentsSelector)

  return useMemo(() => {
    const mainInstrument = instruments.find((i) => i.name === instrument?.name)
    if (!mainInstrument) return null

    const { underlier } = mainInstrument
    const base = underlier.slice(0, underlier.length / 2)
    const quote = underlier.slice(underlier.length / 2)

    const price = side === TradeOrderSideEnum.BUY ? mainInstrument.ask : mainInstrument.bid
    if (!price) return null

    const amountInQuote = price * quantity

    const hasFiatQuote = isFiat(quote)
    const hasFiatBase = isFiat(base)
    const isCryptoCrypto = !hasFiatQuote && !hasFiatBase
    const isCommissionInQuote = hasFiatQuote

    let commissionCurrency: string
    if (hasFiatQuote) {
      commissionCurrency = quote
    } else if (hasFiatBase) {
      commissionCurrency = base
    } else {
      commissionCurrency = base
    }

    let commissionAmount: number
    if (hasFiatQuote) {
      commissionAmount = amountInQuote * commissionRate
    } else {
      commissionAmount = quantity * commissionRate
    }

    const commissionInQuote = hasFiatQuote ? commissionAmount : 0

    return {
      commissionAmount,
      commissionCurrency,
      commissionInQuote,
      isCryptoCrypto,
      isCommissionInQuote,
      amountInQuote,
      price,
      base,
      quote,
    }
  }, [instrument, side, quantity, commissionRate, instruments])
}
